import { Injectable } from '@angular/core';
import { CanActivate, UrlTree } from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { PatientsApiProviderService, ProfileApiProviderService } from '@project/data-providers';
import { UserProfileDataService } from '../user-profile-data.service';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { NotificationsService } from '@lib/notifications';
import { TranslateService } from '@project/translate';
import { LogoutService } from '../logout.service';
import { EProfileTypeDTO } from '@project/view-models';
import { AuthorisationStateService } from '../authorisation-state.service';
import { HcService } from '../hc-service';

/**
 * user's profile is loading via Guard instead of resolver to keep guards/resolvers orders
 * https://github.com/angular/angular/issues/24187
 */

@Injectable({
  providedIn: 'root',
})
export class UserProfileDataLoaderGuard implements CanActivate {
  constructor(
    private userProfileDataService: UserProfileDataService,
    private profileApiProviderService: ProfileApiProviderService,
    private notificationsService: NotificationsService,
    private logoutService: LogoutService,
    private authorisationStateService: AuthorisationStateService,
    private patientsApiProviderService: PatientsApiProviderService,
    private hcService: HcService,
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    if (!!this.userProfileDataService.profile) {
      return of(true);
    }

    return this.profileApiProviderService.getMyProfileInformation().pipe(
      switchMap((profile) => {
        this.userProfileDataService.setProfileData(profile);

        return forkJoin([
          this.isProfileDataFilled(this.authorisationStateService.getAuthorizationData().userType),
          this.isProfileGroupFilled(this.authorisationStateService.getAuthorizationData().userType, profile.id),
        ]);
      }),
      tap(([isProfileDataFilled, isProfileGroupFilled]) => {
        //  Business rule:
        // - When the HC is public, the profile group may be mandatory.
        // - The user must complete the profile group if it is required and still not filled.
        // - The user must complete the profile data if any required profile information is missing or if the mandatory group has not been filled.
        const isPublicHC = this.hcService.getHcConfig().isPublic;
        const shouldFillProfileGroup = !isProfileGroupFilled && isPublicHC;
        const shouldFillProfileData = !isProfileDataFilled || shouldFillProfileGroup;

        // These flags are used to drive redirection and to show/hide UI elements in the complementary data form page.
        this.userProfileDataService.setIsProfileDataFillRequired(shouldFillProfileData);
        this.userProfileDataService.setIsProfileGroupFillRequired(shouldFillProfileGroup);
      }),
      map(() => true),
      catchError(() => {
        this.notificationsService.error({
          message: TranslateService.localize('errors.unable-load-user-data'),
        });
        return this.logoutService.logoutAsGuard();
      }),
    );
  }

  private isProfileDataFilled(profileType: EProfileTypeDTO): Observable<boolean> {
    const flagResolverByProfile: { [profile in EProfileTypeDTO]?: Observable<boolean> } = {
      [EProfileTypeDTO.Patient]: this.profileApiProviderService.isPatientProfileFilled(),
    };

    return flagResolverByProfile[profileType] ?? of(true);
  }

  private isProfileGroupFilled(profileType: EProfileTypeDTO, patientId: string): Observable<boolean> {
    const flagResolverByProfile: { [profile in EProfileTypeDTO]?: Observable<boolean> } = {
      [EProfileTypeDTO.Patient]: this.patientsApiProviderService
        .getPatientTagById(patientId)
        .pipe(map((tags) => Array.isArray(tags) && tags.length > 0)),
    };

    return flagResolverByProfile[profileType] ?? of(false);
  }
}
